// components/project/ProjectEconomicTab.js
// Tab Quadro Economico Interno Cantiere - Solo Admin/Administration

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../../ui/table';
// Tabs sono nel parent ProjectEconomicTab; qui rendo solo il contenuto Analitico
// nota: Tabs import removed in this file - not used
import { toast } from 'sonner';
import { 
  Plus, Calculator, TrendingUp, TrendingDown, Euro, Edit, Copy, Trash2,
  Filter, FileText, Building2, AlertTriangle, ChevronDown, ChevronUp,
  Percent, DollarSign, ShoppingCart, Briefcase, Paperclip, Banknote,
  Download, Package, Sofa
} from 'lucide-react';
import EconomicLineDocumentsModal, { DocumentCountBadge } from '../EconomicLineDocuments';
import EconomicLineInstallmentsModal, { PaymentStatusBadge } from '../EconomicLineInstallments';
import EconomicImportModal from '../EconomicImportModal';
import EconomicImportItemsModal from '../EconomicImportItemsModal';

// Constants
// NON INCLUDERE: pratiche_tecniche, progettazione (non passano economicamente dall'azienda)
const ECONOMIC_CATEGORIES = {
  lavori_edili: 'Lavori edili',
  impianti_idraulici: 'Impianti idraulici',
  impianti_elettrici: 'Impianti elettrici',
  cartongesso: 'Cartongesso e controsoffitti',
  pitture_decorazioni: 'Pitture e decorazioni',
  pavimenti_rivestimenti: 'Pavimenti e rivestimenti',
  serramenti: 'Serramenti e infissi',
  forniture_prodotti: 'Forniture e prodotti',
  arredi: 'Arredi',
  climatizzazione: 'Climatizzazione',
  sicurezza: 'Sicurezza cantiere',
  subappalti: 'Subappalti',
  altro: 'Altro'
};

const COST_TYPES = {
  manodopera: 'Manodopera',
  materiale: 'Materiale',
  fornitura: 'Fornitura',
  subappalto: 'Subappalto',
  servizio: 'Servizio',
  tecnico: 'Tecnico',
  altro: 'Altro'
};

const LINE_STATUSES = {
  stimato: { label: 'Stimato', color: 'bg-gray-100 text-gray-700' },
  confermato: { label: 'Confermato', color: 'bg-blue-100 text-blue-700' },
  ordinato: { label: 'Ordinato', color: 'bg-amber-100 text-amber-700' },
  fatturato: { label: 'Fatturato', color: 'bg-purple-100 text-purple-700' },
  pagato: { label: 'Pagato', color: 'bg-green-100 text-green-700' },
  annullato: { label: 'Annullato', color: 'bg-red-100 text-red-700' }
};

// Tipi origine per badge
const SOURCE_TYPES = {
  manual: { label: 'Manuale', color: 'bg-gray-100 text-gray-600', icon: null },
  contract: { label: 'Contratto', color: 'bg-blue-100 text-blue-700', icon: FileText },
  extra: { label: 'Extra', color: 'bg-orange-100 text-orange-700', icon: Package },
  furniture: { label: 'Arredi', color: 'bg-purple-100 text-purple-700', icon: Sofa },
  estimate: { label: 'Preventivo', color: 'bg-teal-100 text-teal-700', icon: FileText },
  supplier_invoice: { label: 'Fattura', color: 'bg-pink-100 text-pink-700', icon: FileText },
  subcontract: { label: 'Subappalto', color: 'bg-emerald-100 text-emerald-700', icon: FileText },
  grouped_items: { label: 'Voci capitolato', color: 'bg-indigo-100 text-indigo-700', icon: Package }
};

// Stato sorgente contratto subappalto → colore badge
const SUBCONTRACT_SOURCE_STATUS_COLORS = {
  Firmato: 'bg-green-100 text-green-700',
  Risolto: 'bg-red-100 text-red-700',
  Chiuso: 'bg-gray-200 text-gray-700'
};

// Helper per parsing numeri italiani
const parseItalianNumber = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  // Rimuovi spazi e simbolo euro
  let cleaned = String(value).replace(/\s/g, '').replace('€', '').trim();
  // Gestisci formato italiano (1.234,56) o internazionale (1,234.56)
  if (cleaned.includes(',') && cleaned.includes('.')) {
    // Formato italiano: 1.234,56
    if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // Formato internazionale: 1,234.56
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (cleaned.includes(',')) {
    // Solo virgola: potrebbe essere 1234,56 o 1,234
    if (cleaned.split(',')[1]?.length === 2) {
      cleaned = cleaned.replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
  }
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

// Helper per formattazione euro
const formatCurrency = (value) => {
  const num = typeof value === 'number' ? value : parseFloat(value) || 0;
  const n = Math.round(num * 100) === 0 ? 0 : num;
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(n);
};

// Helper per formattazione percentuale
const formatPercent = (value) => {
  const num = typeof value === 'number' ? value : parseFloat(value) || 0;
  return `${num.toFixed(1)}%`;
};

const EconomicAnaliticoView = ({ projectId, user, isAdmin, isAdministration }) => {
  const [lines, setLines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Stato per modal documenti
  const [documentsModalLine, setDocumentsModalLine] = useState(null);
  
  // Stato per modal rate pagamento
  const [installmentsModalLine, setInstallmentsModalLine] = useState(null);
  const [lineInstallmentsSummary, setLineInstallmentsSummary] = useState({});
  
  // Stato per modal import dati ufficiali
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Stato per modal import voci capitolato (FASE 3-bis)
  const [showImportItemsModal, setShowImportItemsModal] = useState(false);
  
  // Filtri
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    cost_type: '',
    supplier_name: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  // QE V2 gerarchico: espansione macro-righe e descrizioni
  const [expandedMacroIds, setExpandedMacroIds] = useState(() => new Set());
  const [expandedDescIds, setExpandedDescIds] = useState(() => new Set());
  const toggleMacro = (id) => setExpandedMacroIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleDesc = (id) => setExpandedDescIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  
  // Form state
  const [formData, setFormData] = useState({
    category: 'altro',
    description: '',
    supplier_name: '',
    assigned_company: '',
    cost_type: 'altro',
    sale_amount: '',
    estimated_cost: '',
    actual_cost: '',
    status: 'stimato',
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    paid_at: '',
    notes: ''
  });

  // Carica dati
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Costruisci query string per filtri
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.cost_type) params.append('cost_type', filters.cost_type);
      if (filters.supplier_name) params.append('supplier_name', filters.supplier_name);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const [linesRes, summaryRes] = await Promise.all([
        fetch(`/api/projects/${projectId}/economic-lines${queryString}`, { credentials: 'include' }),
        fetch(`/api/projects/${projectId}/economic-summary`, { credentials: 'include' })
      ]);
      
      if (linesRes.ok) {
        const linesData = await linesRes.json();
        setLines(linesData);
        
        // Carica installments summary per ogni riga
        const summaries = {};
        await Promise.all(
          linesData.map(async (line) => {
            try {
              const instRes = await fetch(
                `/api/projects/${projectId}/economic-lines/${line.id}/installments`,
                { credentials: 'include' }
              );
              if (instRes.ok) {
                const instData = await instRes.json();
                summaries[line.id] = instData.summary || null;
              }
            } catch (e) {
              console.warn(`Could not load installments for line ${line.id}`);
            }
          })
        );
        setLineInstallmentsSummary(summaries);
      }
      
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }
    } catch (error) {
      console.error('Error loading economic data:', error);
      toast.error('Errore nel caricamento dei dati economici');
    } finally {
      setLoading(false);
    }
  }, [projectId, filters]);

  useEffect(() => {
    if (projectId) {
      loadData();
    }
  }, [projectId, loadData]);

  // KPI direzionali V3 (allineati a QE > Riepilogo): stesse formule certificate, macro-line only.
  //   Costo di commessa = Σ max(assigned_cost, actual_cost) [parent=None]
  //   Margine attuale   = Venduto − Costo di commessa  (NON Venduto − Consuntivato)
  const qeKpi = useMemo(() => {
    const valid = (lines || []).filter(l => l.status !== 'annullato');
    const macro = valid.filter(l => !l.parent_economic_line_id);
    const venduto = Number(summary?.sold_total || 0);
    const affidato = macro.reduce((s, l) => s + Number(l.assigned_cost || 0), 0);
    const consuntivato = macro.reduce((s, l) => s + Number(l.actual_cost || 0), 0);
    const costoDiCommessa = macro.reduce((s, l) => s + Math.max(Number(l.assigned_cost || 0), Number(l.actual_cost || 0)), 0);
    const margineAttuale = venduto - costoDiCommessa;
    const marginalita = venduto > 0 ? (margineAttuale / venduto) * 100 : null;
    const vociRegistrate = summary?.line_count ?? valid.length;
    return { venduto, affidato, consuntivato, costoDiCommessa, margineAttuale, marginalita, vociRegistrate };
  }, [lines, summary]);

  // Reset form
  const resetForm = () => {
    setFormData({
      category: 'altro',
      description: '',
      supplier_name: '',
      assigned_company: '',
      cost_type: 'altro',
      sale_amount: '',
      estimated_cost: '',
      actual_cost: '',
      status: 'stimato',
      invoice_number: '',
      invoice_date: '',
      due_date: '',
      paid_at: '',
      notes: ''
    });
    setEditingLine(null);
  };

  // Apri modal per nuova riga
  const handleNew = () => {
    resetForm();
    setShowModal(true);
  };

  // Apri modal per modifica
  const handleEdit = (line) => {
    setEditingLine(line);
    setFormData({
      category: line.category || 'altro',
      description: line.description || '',
      supplier_name: line.supplier_name || '',
      assigned_company: line.assigned_company || '',
      cost_type: line.cost_type || 'altro',
      sale_amount: line.sale_amount?.toString() || '',
      estimated_cost: line.estimated_cost?.toString() || '',
      actual_cost: line.actual_cost?.toString() || '',
      status: line.status || 'stimato',
      invoice_number: line.invoice_number || '',
      invoice_date: line.invoice_date || '',
      due_date: line.due_date || '',
      paid_at: line.paid_at || '',
      notes: line.notes || ''
    });
    setShowModal(true);
  };

  // Salva riga
  const handleSave = async () => {
    if (!formData.description.trim()) {
      toast.error('La descrizione è obbligatoria');
      return;
    }

    let payload = {
      ...formData,
      sale_amount: parseItalianNumber(formData.sale_amount),
      estimated_cost: parseItalianNumber(formData.estimated_cost),
      actual_cost: parseItalianNumber(formData.actual_cost)
    };

    // Righe Subappalto: i campi economici/identità sono derivati e read-only.
    // Invia SOLO i campi amministrativi ammessi (il backend rifiuta comunque i derivati).
    if (editingLine?.source_type === 'subcontract') {
      const allowed = ['invoice_number', 'invoice_date', 'due_date', 'paid_at', 'notes', 'document_ids'];
      payload = Object.fromEntries(
        Object.entries(payload).filter(([k]) => allowed.includes(k))
      );
    }

    try {
      const url = editingLine 
        ? `/api/projects/${projectId}/economic-lines/${editingLine.id}`
        : `/api/projects/${projectId}/economic-lines`;
      
      const method = editingLine ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingLine ? 'Riga aggiornata' : 'Riga creata');
        setShowModal(false);
        resetForm();
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  // Duplica riga
  const handleDuplicate = async (line) => {
    try {
      const response = await fetch(
        `/api/projects/${projectId}/economic-lines/${line.id}/duplicate`,
        { method: 'POST', credentials: 'include' }
      );
      
      if (response.ok) {
        toast.success('Riga duplicata');
        loadData();
      } else {
        toast.error('Errore nella duplicazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  // Elimina riga
  const handleDelete = async (line) => {
    try {
      const response = await fetch(
        `/api/projects/${projectId}/economic-lines/${line.id}`,
        { method: 'DELETE', credentials: 'include' }
      );
      
      if (response.ok) {
        toast.success('Riga eliminata');
        setDeleteConfirm(null);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  // Verifica se può eliminare (Admin sempre, Administration solo stimato)
  const canDelete = (line) => {
    if (isAdmin) return true;
    if (isAdministration && line.status === 'stimato') return true;
    return false;
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-[#737373]">Caricamento quadro economico...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="qe-analitico-view">
      {/* Barra azioni Analitico */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtri
              {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowImportModal(true)}
              data-testid="import-official-data-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Importa dati ufficiali
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowImportItemsModal(true)}
              data-testid="import-items-btn"
            >
              <Package className="w-4 h-4 mr-2" />
              Importa voci
            </Button>
            <Button onClick={handleNew} data-testid="add-economic-line-btn">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi riga
            </Button>
          </div>

      {/* Summary Cards — semantica V3 allineata a QE > Riepilogo */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="qe-analitico-kpi">
          {/* Venduto */}
          <Card className="card-abitalo bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Euro className="w-4 h-4 text-blue-600" />
                <div>
                  <p className="text-xs text-blue-600">Venduto</p>
                  <p className="font-bold text-blue-700" data-testid="kpi-analitico-venduto">{formatCurrency(qeKpi.venduto)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Costo di commessa */}
          <Card className="card-abitalo bg-gradient-to-br from-orange-50 to-orange-100">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-[#FF7819]" />
                <div>
                  <p className="text-xs text-[#B25400]">Costo di commessa</p>
                  <p className="font-bold text-[#B25400]" data-testid="kpi-analitico-costo-commessa">{formatCurrency(qeKpi.costoDiCommessa)}</p>
                  <p className="text-[10px] text-[#B25400]/70">max(affidato, consuntivo)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Margine attuale */}
          <Card className={`card-abitalo ${qeKpi.margineAttuale >= 0 ? 'bg-gradient-to-br from-teal-50 to-teal-100' : 'bg-gradient-to-br from-red-50 to-red-100'}`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                {qeKpi.margineAttuale >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-teal-600" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-600" />
                )}
                <div>
                  <p className={`text-xs ${qeKpi.margineAttuale >= 0 ? 'text-teal-600' : 'text-red-600'}`}>Margine attuale</p>
                  <p className={`font-bold ${qeKpi.margineAttuale >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-testid="kpi-analitico-margine">
                    {formatCurrency(qeKpi.margineAttuale)}
                  </p>
                  <p className={`text-xs ${qeKpi.margineAttuale >= 0 ? 'text-teal-500' : 'text-red-500'}`}>
                    {qeKpi.marginalita == null ? '—' : formatPercent(qeKpi.marginalita)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Affidato corrente */}
          <Card className="card-abitalo">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Affidato corrente</p>
                  <p className="font-semibold" data-testid="kpi-analitico-affidato">{formatCurrency(qeKpi.affidato)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Consuntivato */}
          <Card className="card-abitalo">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-amber-500" />
                <div>
                  <p className="text-xs text-amber-600">Consuntivato</p>
                  <p className="font-semibold text-amber-700" data-testid="kpi-analitico-consuntivato">{formatCurrency(qeKpi.consuntivato)}</p>
                  <p className="text-[10px] text-amber-500/80">fatturato fornitori</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Voci registrate */}
          <Card className="card-abitalo">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-500" />
                <div>
                  <p className="text-xs text-purple-600">Voci registrate</p>
                  <p className="font-semibold text-purple-700" data-testid="kpi-analitico-voci">{qeKpi.vociRegistrate}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtri */}
      {showFilters && (
        <Card className="card-abitalo">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs">Categoria</Label>
                <Select value={filters.category} onValueChange={(v) => setFilters(f => ({ ...f, category: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Tutte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Tutte</SelectItem>
                    {Object.entries(ECONOMIC_CATEGORIES).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Stato</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Tutti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Tutti</SelectItem>
                    {Object.entries(LINE_STATUSES).map(([value, { label }]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tipo costo</Label>
                <Select value={filters.cost_type} onValueChange={(v) => setFilters(f => ({ ...f, cost_type: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Tutti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Tutti</SelectItem>
                    {Object.entries(COST_TYPES).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Fornitore</Label>
                <Input
                  className="mt-1"
                  placeholder="Cerca fornitore..."
                  value={filters.supplier_name}
                  onChange={(e) => setFilters(f => ({ ...f, supplier_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm" onClick={() => setFilters({ category: '', status: '', cost_type: '', supplier_name: '' })}>
                Resetta filtri
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabella */}
      {lines.length === 0 ? (
        <Card className="card-abitalo">
          <CardContent className="p-8 text-center">
            <Calculator className="w-12 h-12 text-[#DCDCDC] mx-auto mb-3" />
            <p className="text-[#737373] mb-4">Nessuna voce economica registrata</p>
            <Button onClick={handleNew}>
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi prima voce
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-abitalo overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[120px]">Categoria</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Fornitore</TableHead>
                  <TableHead className="w-[90px]">Tipo</TableHead>
                  <TableHead className="text-right w-[110px]">Vendita</TableHead>
                  <TableHead className="text-right w-[110px]">Costo prev.</TableHead>
                  <TableHead className="text-right w-[110px]" title="Costo affidato tramite subappalto firmato">Affidato</TableHead>
                  <TableHead className="text-right w-[110px]">Costo cons.</TableHead>
                  <TableHead className="text-right w-[100px]">Margine prev.</TableHead>
                  <TableHead className="text-right w-[100px]">Margine cons.</TableHead>
                  <TableHead className="w-[90px]">Stato</TableHead>
                  <TableHead className="w-[70px] text-center">Doc.</TableHead>
                  <TableHead className="w-[50px] text-center">Rate</TableHead>
                  <TableHead className="text-right w-[90px]">Pagato</TableHead>
                  <TableHead className="text-right w-[90px]">Residuo</TableHead>
                  <TableHead className="w-[80px]">Pag.</TableHead>
                  <TableHead className="w-[100px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const childrenByParent = {};
                  lines.forEach((l) => { if (l.parent_economic_line_id) { (childrenByParent[l.parent_economic_line_id] = childrenByParent[l.parent_economic_line_id] || []).push(l); } });
                  const macroLines = lines.filter((l) => !l.parent_economic_line_id);
                  const visibleRows = [];
                  macroLines.forEach((m) => {
                    const kids = childrenByParent[m.id] || [];
                    visibleRows.push({ line: m, isChild: false, hasChildren: kids.length > 0 });
                    if (expandedMacroIds.has(m.id)) kids.forEach((c) => visibleRows.push({ line: c, isChild: true, hasChildren: false }));
                  });
                  return visibleRows;
                })().map(({ line, isChild, hasChildren }) => (
                  <TableRow key={line.id} data-testid={isChild ? `qe-child-row-${line.id}` : `qe-macro-row-${line.id}`} className={`${line.status === 'annullato' ? 'opacity-50' : ''} ${isChild ? 'bg-slate-50/70' : ''}`}>
                    <TableCell className="text-xs">
                      {ECONOMIC_CATEGORIES[line.category] || line.category}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2" style={{ paddingLeft: isChild ? 22 : 0 }}>
                          {hasChildren && (
                            <button type="button" onClick={() => toggleMacro(line.id)} data-testid={`qe-expand-${line.id}`} className="text-slate-400 hover:text-slate-700 shrink-0">
                              <ChevronDown className={`w-4 h-4 transition-transform ${expandedMacroIds.has(line.id) ? '' : '-rotate-90'}`} />
                            </button>
                          )}
                          <p className={`font-medium text-sm ${isChild ? 'text-slate-600' : ''} ${expandedDescIds.has(line.id) ? '' : 'line-clamp-2'} ${isChild ? 'cursor-pointer' : ''}`}
                             onClick={isChild ? () => toggleDesc(line.id) : undefined}>{line.description}</p>
                          {/* Badge origine se non manuale */}
                          {line.source_type && line.source_type !== 'manual' && (
                            <Badge className={`text-[10px] px-1.5 py-0 ${SOURCE_TYPES[line.source_type]?.color || 'bg-gray-100'}`}>
                              {SOURCE_TYPES[line.source_type]?.label || line.source_type}
                              {line.source_type === 'grouped_items' && line.source_items_count > 0 && (
                                <span className="ml-1">({line.source_items_count})</span>
                              )}
                            </Badge>
                          )}
                          {/* Stato sorgente contratto subappalto (Firmato/Risolto/Chiuso) */}
                          {line.source_type === 'subcontract' && line.source_status_label && (
                            <Badge
                              className={`text-[10px] px-1.5 py-0 ${SUBCONTRACT_SOURCE_STATUS_COLORS[line.source_status_label] || 'bg-gray-100 text-gray-600'}`}
                              data-testid={`economic-line-subcontract-status-${line.id}`}
                            >
                              {line.source_status_label}
                            </Badge>
                          )}
                        </div>
                        {line.notes && (
                          <p className="text-xs text-gray-400 truncate max-w-[200px]">{line.notes}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {line.supplier_name && (
                        <div className="flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-gray-400" />
                          <span className="text-sm">{line.supplier_name}</span>
                        </div>
                      )}
                      {line.assigned_company && !line.supplier_name && (
                        <span className="text-sm text-gray-500">{line.assigned_company}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {COST_TYPES[line.cost_type] || line.cost_type}
                    </TableCell>
                    <TableCell className="text-right font-medium text-blue-600">
                      {line.source_type === 'subcontract' ? (
                        <div
                          className="flex flex-col items-end"
                          title="Quota del valore cliente riferibile alle lavorazioni affidate. Non incrementa il Venduto del progetto."
                          data-testid={`line-attributed-sale-${line.id}`}
                        >
                          <span>{formatCurrency(line.attributed_sale_current || 0)}</span>
                          <span className="text-[10px] text-gray-400 font-normal leading-none">attribuita</span>
                        </div>
                      ) : (
                        formatCurrency(line.sale_amount)
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        // QE V3.2 §47/§57: subappalto = Costo previsto = BUDGET da scope cliente (estimated_cost), non l'affidato.
                        if (line.source_type === 'subcontract') {
                          if (line.expected_cost_status === 'needs_review') {
                            return (
                              <span className="inline-flex flex-col items-end" data-testid={`line-cost-prev-review-${line.id}`}>
                                <span className="text-xs text-gray-300">—</span>
                                <span className="mt-0.5 inline-flex items-center rounded px-1 py-0.5 text-[9px] font-medium bg-amber-100 text-amber-700"
                                  title="Scope cliente non deterministico · da verificare">Da verificare</span>
                              </span>
                            );
                          }
                          const comps = line.expected_cost_components || [];
                          const breakdown = comps.map(c => `${c.category_code}: vend. cliente ${formatCurrency(c.client_scope_sale_amount)} · min ${c.minimum_margin_pct}% · budget ${formatCurrency(c.expected_cost)}`).join('\n');
                          const tip = `Budget da scope cliente\nVenduto scope: ${formatCurrency(line.expected_cost_client_scope || 0)}\n${breakdown}`;
                          return (
                            <span className="inline-flex flex-col items-end">
                              <span title={tip} data-testid={`line-cost-prev-${line.id}`} className={comps.length ? 'underline decoration-dotted decoration-gray-300 cursor-help' : undefined}>
                                {formatCurrency(line.estimated_cost || 0)}
                              </span>
                              {line.over_budget && (
                                <span className="mt-0.5 inline-flex items-center rounded px-1 py-0.5 text-[9px] font-medium bg-red-100 text-red-700"
                                  title={`Affidato ${formatCurrency(line.assigned_cost)} oltre il budget da scope ${formatCurrency(line.estimated_cost)}`}
                                  data-testid={`line-over-budget-${line.id}`}>Sopra budget</span>
                              )}
                            </span>
                          );
                        }
                        const src = line.expected_cost_source;
                        const status = line.expected_cost_status;
                        if (status === 'partial' || status === 'needs_review') {
                          const unknown = line.expected_cost_unknown_sale || 0;
                          return (
                            <span className="inline-flex flex-col items-end" data-testid={`line-cost-prev-review-${line.id}`}>
                              <span className="text-xs text-gray-300">—</span>
                              <span className={`mt-0.5 inline-flex items-center rounded px-1 py-0.5 text-[9px] font-medium ${status === 'partial' ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-700'}`}
                                title={status === 'partial' ? `Budget incompleto · da classificare ${formatCurrency(unknown)}` : 'Contratto non riconciliato · da verificare'}>
                                {status === 'partial' ? 'Budget incompleto' : 'Da verificare'}
                              </span>
                            </span>
                          );
                        }
                        const uninit = !line.estimated_cost && src !== 'manual' && src !== 'category_default';
                        if (uninit) return <span className="text-xs text-gray-300" title="Costo previsto non inizializzato" data-testid={`line-cost-prev-unknown-${line.id}`}>—</span>;
                        const comps = line.expected_cost_components || [];
                        let tip;
                        if (src === 'manual') tip = 'Manuale';
                        else if (src === 'category_default' && comps.length) {
                          tip = comps.map(c => `${c.category_code}: vend. orig. ${formatCurrency(c.source_sale_amount)}${c.discount_pct ? ` · sconto ${c.discount_pct}%` : ''} · vend. alloc. ${formatCurrency(c.allocated_sale_amount)} · min ${c.minimum_margin_pct}% · costo ${formatCurrency(c.expected_cost)}`).join('\n');
                        } else if (src === 'category_default') tip = `Default categoria ${line.expected_cost_category || ''}`;
                        return (
                          <span title={tip} data-testid={`line-cost-prev-${line.id}`} className={comps.length ? 'underline decoration-dotted decoration-gray-300 cursor-help' : undefined}>
                            {formatCurrency(line.estimated_cost)}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right" data-testid={`line-assigned-${line.id}`}>
                      {(line.assigned_cost || 0) > 0 ? (
                        <div className="flex flex-col items-end">
                          <span className="font-medium text-[#FF7819]">{formatCurrency(line.assigned_cost)}</span>
                          {line.assigned_at && (
                            <span className="text-[10px] text-gray-400" title={line.assigned_at}>
                              {new Date(line.assigned_at).toLocaleDateString('it-IT')}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(line.actual_cost)}
                    </TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        const isSub = line.source_type === 'subcontract';
                        // QE V3.1 §13/§24/§49: non-inizializzato o partial/needs_review -> Margine prev. "—" (NON 100%).
                        const src = line.expected_cost_source;
                        const status = line.expected_cost_status;
                        const uninit = !isSub && (!line.estimated_cost || status === 'partial' || status === 'needs_review') && src !== 'manual';
                        if (uninit) {
                          return (
                            <>
                              <span className="text-gray-300" data-testid={`line-margin-prev-${line.id}`}>—</span>
                              <br />
                              <span className="text-xs text-gray-400">—</span>
                            </>
                          );
                        }
                        // Margine prev. coerente col Costo prev. mostrato: Vendita - estimated_cost.
                        let m, p;
                        if (isSub) {
                          m = line.attributed_expected_margin;
                          p = line.attributed_expected_margin_percent;
                        } else if ((src === 'category_default' || src === 'manual') && line.estimated_cost != null) {
                          const sale = line.sale_amount || 0;
                          m = sale - line.estimated_cost;
                          p = sale > 0 ? (m / sale) * 100 : null;
                        } else {
                          m = line.margin_estimated;
                          p = line.margin_estimated_percent;
                        }
                        const belowMin = !isSub && src === 'manual' && line.expected_cost_min_margin != null && p != null && (p / 100) < line.expected_cost_min_margin;
                        return (
                          <>
                            <span className={(m || 0) >= 0 ? 'text-green-600' : 'text-red-600'} data-testid={`line-margin-prev-${line.id}`}>
                              {formatCurrency(m)}
                            </span>
                            {belowMin && (
                              <span className="ml-1 inline-flex items-center rounded px-1 py-0.5 text-[9px] font-medium bg-amber-100 text-amber-700 align-middle"
                                title={`Margine previsto ${formatPercent(p)} · sotto il minimo categoria ${Math.round(line.expected_cost_min_margin * 100)}%`}
                                data-testid={`line-below-min-${line.id}`}>Sotto minimo</span>
                            )}
                            <br />
                            <span className="text-xs text-gray-400">
                              {p == null ? '—' : formatPercent(p)}
                            </span>
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        const isSub = line.source_type === 'subcontract';
                        const m = isSub ? line.attributed_actual_margin : line.margin_actual;
                        const p = isSub ? line.attributed_actual_margin_percent : line.margin_actual_percent;
                        return (
                          <>
                            <span className={(m || 0) >= 0 ? 'text-green-600' : 'text-red-600'} data-testid={`line-margin-cons-${line.id}`}>
                              {formatCurrency(m)}
                            </span>
                            <br />
                            <span className="text-xs text-gray-400">
                              {p == null ? '—' : formatPercent(p)}
                            </span>
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-1 rounded ${LINE_STATUSES[line.status]?.color || 'bg-gray-100'}`}>
                        {LINE_STATUSES[line.status]?.label || line.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <DocumentCountBadge 
                        count={line.document_ids?.length || 0}
                        onClick={() => setDocumentsModalLine(line)}
                      />
                    </TableCell>
                    {/* Colonne Rate Pagamento */}
                    <TableCell className="text-center">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 px-2"
                        onClick={() => setInstallmentsModalLine(line)}
                        title="Gestisci rate pagamento"
                        data-testid={`open-installments-${line.id}`}
                      >
                        <Banknote className="w-4 h-4 mr-1 text-gray-500" />
                        <span className="text-xs font-medium">
                          {lineInstallmentsSummary[line.id]?.installments_count || 0}
                        </span>
                      </Button>
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {lineInstallmentsSummary[line.id]?.installments_paid_total > 0 ? (
                        <span className="text-green-600">
                          {formatCurrency(lineInstallmentsSummary[line.id]?.installments_paid_total || 0)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {(() => {
                        // RESIDUO V3 = costo conosciuto ancora da pagare = max(max(assigned,actual) − pagato, 0).
                        // paid = stessa source canonica della colonna "Pagato" (installments_paid_total).
                        const costBasis = Math.max(Number(line.assigned_cost || 0), Number(line.actual_cost || 0));
                        const paid = Number(lineInstallmentsSummary[line.id]?.installments_paid_total || 0);
                        const residuo = Math.max(costBasis - paid, 0);
                        return residuo > 0 ? (
                          <span className="text-orange-600" data-testid={`residuo-${line.id}`}>{formatCurrency(residuo)}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {lineInstallmentsSummary[line.id]?.payment_status && 
                       lineInstallmentsSummary[line.id]?.payment_status !== 'nessuna_rata' ? (
                        <PaymentStatusBadge 
                          status={lineInstallmentsSummary[line.id]?.payment_status}
                          count={lineInstallmentsSummary[line.id]?.installments_count || 0}
                        />
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(line)} title="Modifica">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDuplicate(line)} title="Duplica">
                          <Copy className="w-4 h-4" />
                        </Button>
                        {canDelete(line) && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => setDeleteConfirm(line)}
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Riga totali (solo macro-righe, i figli detail non contribuiscono) */}
                <TableRow className="bg-gray-100 font-semibold">
                  <TableCell colSpan={4} className="text-right">TOTALI</TableCell>
                  <TableCell className="text-right text-blue-700">
                    {formatCurrency(lines.filter(l => !l.parent_economic_line_id && l.status !== 'annullato').reduce((sum, l) => sum + (l.sale_amount || 0), 0))}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(lines.filter(l => !l.parent_economic_line_id && l.status !== 'annullato').reduce((sum, l) => sum + (l.estimated_cost || 0), 0))}
                  </TableCell>
                  <TableCell className="text-right text-[#FF7819]" data-testid="totals-assigned">
                    {formatCurrency(lines.filter(l => !l.parent_economic_line_id && l.status !== 'annullato').reduce((sum, l) => sum + (l.assigned_cost || 0), 0))}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(lines.filter(l => !l.parent_economic_line_id && l.status !== 'annullato').reduce((sum, l) => sum + (l.actual_cost || 0), 0))}
                  </TableCell>
                  <TableCell className="text-right text-green-700">
                    {formatCurrency(lines.filter(l => !l.parent_economic_line_id && l.status !== 'annullato').reduce((sum, l) => sum + (l.margin_estimated || 0), 0))}
                  </TableCell>
                  <TableCell className="text-right text-green-700">
                    {formatCurrency(lines.filter(l => !l.parent_economic_line_id && l.status !== 'annullato').reduce((sum, l) => sum + (l.margin_actual || 0), 0))}
                  </TableCell>
                  {/* Totali rate pagamento */}
                  <TableCell colSpan={2}></TableCell>
                  <TableCell className="text-center text-sm">
                    {summary?.installments_count || 0}
                  </TableCell>
                  <TableCell className="text-right text-sm text-green-600">
                    {summary?.installments_paid_total > 0 ? formatCurrency(summary.installments_paid_total) : '-'}
                  </TableCell>
                  <TableCell className="text-right text-sm text-orange-600">
                    {(() => {
                      // Totale Residuo V3 = Σ per macro-line max(max(assigned,actual) − pagato, 0)
                      const totResiduo = lines
                        .filter(l => !l.parent_economic_line_id && l.status !== 'annullato')
                        .reduce((sum, l) => {
                          const costBasis = Math.max(Number(l.assigned_cost || 0), Number(l.actual_cost || 0));
                          const paid = Number(lineInstallmentsSummary[l.id]?.installments_paid_total || 0);
                          return sum + Math.max(costBasis - paid, 0);
                        }, 0);
                      return totResiduo > 0 ? formatCurrency(totResiduo) : '-';
                    })()}
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Info box */}
      <Card className="card-abitalo bg-purple-50 border-purple-200">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-purple-800">
              <p className="font-medium mb-1">Quadro Economico Riservato</p>
              <p>Questo modulo e visibile solo ad Admin e Amministrazione. I dati qui inseriti non sono collegati automaticamente ai documenti ufficiali del progetto e servono per il controllo interno della marginalita di cantiere.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal Aggiungi/Modifica */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingLine ? 'Modifica voce economica' : 'Nuova voce economica'}
            </DialogTitle>
            <DialogDescription>
              Inserisci i dati della voce di costo/ricavo per il quadro economico del cantiere.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {editingLine?.source_type === 'subcontract' && (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800" data-testid="subcontract-readonly-notice">
                Voce <strong>Subappalto</strong>: i valori economici (Vendita attribuita, Costo previsto, Affidato, Consuntivato), l'anagrafica e lo <strong>stato</strong> sono derivati dal contratto e non modificabili manualmente. Solo le note restano editabili.
              </div>
            )}
            {/* Riga 1: Categoria e Tipo costo */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(f => ({ ...f, category: v }))} disabled={editingLine?.source_type === 'subcontract'}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ECONOMIC_CATEGORIES).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo costo</Label>
                <Select value={formData.cost_type} onValueChange={(v) => setFormData(f => ({ ...f, cost_type: v }))} disabled={editingLine?.source_type === 'subcontract'}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(COST_TYPES).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Descrizione */}
            <div className="space-y-2">
              <Label>Descrizione *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                placeholder="es. Demolizione pavimenti esistenti"
                disabled={editingLine?.source_type === 'subcontract'}
              />
            </div>

            {/* Riga 2: Fornitore e Affidatario */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fornitore</Label>
                <Input
                  value={formData.supplier_name}
                  onChange={(e) => setFormData(f => ({ ...f, supplier_name: e.target.value }))}
                  placeholder="es. Edilbari Srl"
                  disabled={editingLine?.source_type === 'subcontract'}
                />
              </div>
              <div className="space-y-2">
                <Label>Azienda affidataria</Label>
                <Input
                  value={formData.assigned_company}
                  onChange={(e) => setFormData(f => ({ ...f, assigned_company: e.target.value }))}
                  placeholder="es. Impresa Rossi"
                  disabled={editingLine?.source_type === 'subcontract'}
                />
              </div>
            </div>

            {/* Riga 3: Importi */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Vendita cliente (EUR)</Label>
                <Input
                  type="text"
                  value={formData.sale_amount}
                  onChange={(e) => setFormData(f => ({ ...f, sale_amount: e.target.value }))}
                  placeholder="0,00"
                  className="text-right"
                  disabled={editingLine?.source_type === 'subcontract'}
                />
              </div>
              <div className="space-y-2">
                <Label>Costo previsto (EUR)</Label>
                <Input
                  type="text"
                  value={formData.estimated_cost}
                  onChange={(e) => setFormData(f => ({ ...f, estimated_cost: e.target.value }))}
                  placeholder="0,00"
                  className="text-right"
                  disabled={editingLine?.source_type === 'subcontract'}
                />
              </div>
              <div className="space-y-2">
                <Label>Costo consuntivo (EUR)</Label>
                <Input
                  type="text"
                  value={formData.actual_cost}
                  onChange={(e) => setFormData(f => ({ ...f, actual_cost: e.target.value }))}
                  placeholder="0,00"
                  className="text-right"
                  disabled={editingLine?.source_type === 'subcontract'}
                />
              </div>
            </div>

            {/* Preview margini */}
            {(formData.sale_amount || formData.estimated_cost || formData.actual_cost) && (
              <Card className="bg-gray-50">
                <CardContent className="p-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Margine previsto:</p>
                      <p className={`font-semibold ${(parseItalianNumber(formData.sale_amount) - parseItalianNumber(formData.estimated_cost)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(parseItalianNumber(formData.sale_amount) - parseItalianNumber(formData.estimated_cost))}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Margine consuntivo:</p>
                      <p className={`font-semibold ${(parseItalianNumber(formData.sale_amount) - parseItalianNumber(formData.actual_cost)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(parseItalianNumber(formData.sale_amount) - parseItalianNumber(formData.actual_cost))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stato */}
            <div className="space-y-2">
              <Label>Stato</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData(f => ({ ...f, status: v }))} disabled={editingLine?.source_type === 'subcontract'}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LINE_STATUSES).map(([value, { label }]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dati fattura */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>N. Fattura fornitore</Label>
                <Input
                  value={formData.invoice_number}
                  onChange={(e) => setFormData(f => ({ ...f, invoice_number: e.target.value }))}
                  placeholder="es. FT-2026/001"
                />
              </div>
              <div className="space-y-2">
                <Label>Data fattura</Label>
                <Input
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => setFormData(f => ({ ...f, invoice_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Scadenza pagamento</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(f => ({ ...f, due_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Data pagamento</Label>
                <Input
                  type="date"
                  value={formData.paid_at}
                  onChange={(e) => setFormData(f => ({ ...f, paid_at: e.target.value }))}
                />
              </div>
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label>Note interne</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))}
                placeholder="Note aggiuntive..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
              Annulla
            </Button>
            <Button onClick={handleSave}>
              {editingLine ? 'Salva modifiche' : 'Crea voce'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Conferma Eliminazione */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare la voce "{deleteConfirm?.description}"? 
              Questa azione non puo essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Documenti Riga */}
      <EconomicLineDocumentsModal
        isOpen={!!documentsModalLine}
        onClose={() => setDocumentsModalLine(null)}
        projectId={projectId}
        line={documentsModalLine}
        onDocumentChange={loadData}
      />

      {/* Modal Rate Pagamento */}
      <EconomicLineInstallmentsModal
        isOpen={!!installmentsModalLine}
        onClose={() => setInstallmentsModalLine(null)}
        projectId={projectId}
        line={installmentsModalLine}
        linkedDocuments={installmentsModalLine?.document_ids?.length > 0 ? [] : []} 
        onInstallmentChange={loadData}
      />

      {/* Modal Import Dati Ufficiali */}
      <EconomicImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        projectId={projectId}
        onImportComplete={(result) => {
          loadData();
          if (result.imported_count > 0) {
            setShowImportModal(false);
          }
        }}
      />

      {/* Modal Import Voci Capitolato (FASE 3-bis) */}
      <EconomicImportItemsModal
        isOpen={showImportItemsModal}
        onClose={() => setShowImportItemsModal(false)}
        projectId={projectId}
        onImportComplete={(result) => {
          loadData();
          if (result.success) {
            setShowImportItemsModal(false);
          }
        }}
      />
    </div>
  );
};

export default EconomicAnaliticoView;
